import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { SeoService } from '../../../core/services/seo.service';
import { KontaktService, KontaktThema } from './kontakt.service';

interface KontaktKategorie {
  icon: 'mail' | 'press' | 'partner' | 'shield' | 'gavel' | 'flag';
  title: string;
  email?: string;
  link?: { route: string; label: string };
  hint?: string;
}

interface ThemaOption {
  value: KontaktThema;
  label: string;
}

const THEMEN: ThemaOption[] = [
  { value: 'allgemein', label: 'Allgemein' },
  { value: 'presse', label: 'Presse' },
  { value: 'kooperation', label: 'Kooperation' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

@Component({
  selector: 'app-kontakt',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './kontakt.component.html',
  styleUrl: './kontakt.component.scss',
})
export class KontaktComponent {
  private readonly kontakt = inject(KontaktService);

  readonly themen = THEMEN;

  readonly kategorien: KontaktKategorie[] = [
    {
      icon: 'mail',
      title: 'Allgemein',
      email: 'info@kulatrix.com',
      hint: 'Sonstige Anfragen, die zu keiner anderen Kategorie passen.',
    },
    {
      icon: 'press',
      title: 'Presse & Medien',
      email: 'presse@kulatrix.com',
      hint: 'Pressemappe und O-Töne auf Anfrage.',
    },
    {
      icon: 'partner',
      title: 'Kooperationen',
      email: 'partner@kulatrix.com',
      hint: 'Partnerschaften, Innungen, Verbände.',
    },
    {
      icon: 'shield',
      title: 'Datenschutz',
      email: 'datenschutz@kulatrix.com',
      hint: 'Auskünfte und Betroffenenrechte gemäß DSGVO.',
    },
    {
      icon: 'gavel',
      title: 'Rechtliches',
      email: 'legal@kulatrix.com',
      hint: 'Behörden-Anfragen und Rechtsfragen.',
    },
    {
      icon: 'flag',
      title: 'Inhalte melden',
      link: { route: '/melden', label: 'Notice & Action öffnen' },
      hint: 'Notice & Action gemäß DSA Art. 16.',
    },
  ];

  // Form-State
  thema: KontaktThema | '' = '';
  name = '';
  email = '';
  anliegen = '';
  consent = false;
  honeypot = '';

  readonly status = signal<'idle' | 'submitting' | 'success' | 'error'>('idle');
  readonly errorMessage = signal<string | null>(null);
  readonly successTicket = signal<{ id: string } | null>(null);

  constructor() {
    inject(SeoService).apply({
      title: 'Kontakt · Craftly',
      description:
        'Spezialisierte Adressen für Presse, Kooperationen, Datenschutz und Behörden. Plus Kontaktformular für sonstige Anliegen.',
      path: '/kontakt',
      type: 'website',
    });
  }

  bodyValid(): boolean {
    const t = this.anliegen.trim().length;
    return t >= 30 && t <= 2000;
  }

  bodyHint(): string {
    const len = this.anliegen.trim().length;
    if (len === 0) return 'Min. 30 Zeichen';
    if (len < 30) return `${30 - len} Zeichen fehlen`;
    return `${len} / 2000`;
  }

  async onSubmit(form: NgForm) {
    if (this.status() === 'submitting') return;
    this.errorMessage.set(null);

    if (!form.valid || !this.bodyValid() || !this.consent || !this.thema) {
      Object.values(form.controls).forEach((c) => c.markAsTouched());
      return;
    }

    this.status.set('submitting');
    try {
      const res = await this.kontakt.createAnfrage({
        thema: this.thema as KontaktThema,
        name: this.name.trim(),
        email: this.email.trim(),
        anliegen: this.anliegen.trim(),
        consentDataPrivacy: this.consent,
        honeypot: this.honeypot,
      });
      this.successTicket.set({ id: res.ticketId });
      this.status.set('success');
    } catch (err: any) {
      this.status.set('error');
      const msg = err?.error?.error ?? err?.message ?? 'Beim Senden ist etwas schiefgelaufen.';
      this.errorMessage.set(msg);
    }
  }
}
